import {
  Box,
  Button,
  Typography,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LockClockIcon from '@mui/icons-material/LockClock';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/useIsMobile';
import { UserAvatar, RegistrationPlate } from '@/components/common';
import { channelColors } from '@/styles/theme';
import { splitRemaining } from '@/utils/sessionWindow';
import { getConversationPartyPhone, getConversationTitle } from '@/utils/conversationParty';
import { mailboxCategoryLabelKey } from './mailboxCategory';
import type { Conversation, MessageChannel } from '@/types';

interface ConversationHeaderProps {
  conversation: Conversation;
  onBack?: () => void;
  /** Mailbox email threads (#3736): opens the link-to-customer dialog. */
  onLinkCustomer?: () => void;
}

const channelConfig: Record<MessageChannel, { icon: React.ReactNode; labelKey: string; color: string }> = {
  whatsapp: { icon: <WhatsAppIcon sx={{ fontSize: 16 }} />, labelKey: 'channelWhatsApp', color: channelColors.whatsapp },
  sms: { icon: <SmsIcon sx={{ fontSize: 16 }} />, labelKey: 'channelSms', color: channelColors.sms },
  email: { icon: <EmailIcon sx={{ fontSize: 16 }} />, labelKey: 'channelEmail', color: channelColors.email },
  internal: { icon: null, labelKey: 'channelInternal', color: channelColors.internal },
  portal: { icon: <LanguageIcon sx={{ fontSize: 16 }} />, labelKey: 'channelPortal', color: channelColors.portal },
};

export function ConversationHeader({ conversation, onBack, onLinkCustomer }: ConversationHeaderProps) {
  const { t } = useTranslation('messages');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // The channel is whatever the API stored — an unknown one must not take the page down.
  const channel = channelConfig[conversation.channel] ?? channelConfig.internal;
  const partyName = getConversationTitle(conversation, t('unknownContact'));
  const email = conversation.channel === 'email' ? conversation.email : null;
  const partyPhone = getConversationPartyPhone(conversation.customer);

  // WhatsApp's 24 h window decides whether a free-form reply is even possible,
  // so it belongs next to the customer, not behind a failed send (#1471).
  const sessionWindow = conversation.channel === 'whatsapp' ? conversation.sessionWindow : null;
  const remaining = splitRemaining(sessionWindow?.minutesRemaining ?? 0);
  const sessionWindowLabel = !sessionWindow
    ? null
    : !sessionWindow.active
      ? t('sessionWindowClosed')
      : remaining.hours > 0
        ? t('sessionWindowOpenHours', { hours: remaining.hours, minutes: remaining.minutes })
        : t('sessionWindowOpenMinutes', { minutes: remaining.minutes });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: { xs: 1.5, md: 2 },
        gap: 1,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        minHeight: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
        {isMobile && onBack && (
          <Tooltip title={t('back')}>
            <IconButton onClick={onBack} edge="start" sx={{ mr: 0.5, minWidth: 44, minHeight: 44 }} aria-label={t('back')}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}
        {!isMobile && (
          <UserAvatar
            name={partyName}
            variant="rounded"
            colorScheme={conversation.customer.customerType === 'business' ? 'business' : 'default'}
          />
        )}

        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight={600} noWrap>
              {partyName}
            </Typography>
            <Chip
              icon={channel.icon as React.ReactElement}
              label={t(channel.labelKey)}
              size="small"
              sx={{
                height: 22,
                '& .MuiChip-icon': { color: channel.color },
                '& .MuiChip-label': { fontSize: '0.7rem' },
              }}
            />
            {email?.mailboxCategory && (
              <Chip
                label={t(mailboxCategoryLabelKey[email.mailboxCategory])}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.7rem' } }}
              />
            )}
            {conversation.customerPhase === 'awaiting_workshop' && (
              <Chip
                label={t('awaitingWorkshop')}
                size="small"
                color="info"
                sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.7rem' } }}
              />
            )}
            {conversation.requiresHuman && conversation.customerPhase !== 'awaiting_workshop' && (
              <Tooltip title={t('requiresHumanHint')}>
                <Chip
                  icon={<SupportAgentIcon sx={{ fontSize: 14 }} />}
                  label={t('requiresHuman')}
                  size="small"
                  color="warning"
                  sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.7rem' } }}
                />
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            {email && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {[email.subject || t('emailNoSubject'), email.linked ? email.address : null]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            )}
            {partyPhone && partyName !== partyPhone && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {partyPhone}
              </Typography>
            )}
            {!isMobile && conversation.vehicle && (
              <>
                {partyPhone && partyName !== partyPhone && (
                  <Typography variant="caption" color="text.secondary">
                    •
                  </Typography>
                )}
                <RegistrationPlate registrationNumber={conversation.vehicle.registrationNumber ?? '-'} />
              </>
            )}
            {sessionWindow && sessionWindowLabel && (
              <Tooltip
                title={sessionWindow.active ? t('sessionWindowOpenHint') : t('sessionWindowClosedHint')}
              >
                <Chip
                  icon={
                    sessionWindow.active ? (
                      <ScheduleIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <LockClockIcon sx={{ fontSize: 14 }} />
                    )
                  }
                  label={sessionWindowLabel}
                  size="small"
                  color={sessionWindow.active ? 'success' : 'warning'}
                  variant="outlined"
                  sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.7rem' } }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {!isMobile && conversation.workOrderNumber && (
          <Tooltip title={t('viewWorkOrder')}>
            <Chip
              icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
              label={conversation.workOrderNumber}
              size="small"
              clickable
              onClick={() => navigate(`/workorders/${conversation.workOrderId}`)}
              sx={{ mr: 1 }}
            />
          </Tooltip>
        )}

        {email && onLinkCustomer && (
          <Button
            size="small"
            variant={email.linked ? 'text' : 'outlined'}
            startIcon={<PersonAddAlt1Icon />}
            onClick={onLinkCustomer}
          >
            {email.linked ? t('emailRelink') : t('emailLink')}
          </Button>
        )}

        {partyPhone && (
          <Tooltip title={t('call')}>
            <IconButton
              size="small"
              aria-label={t('call')}
              sx={{ minWidth: 44, minHeight: 44 }}
              component="a"
              href={`tel:${partyPhone}`}
            >
              <PhoneIcon />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={t('moreOptions')}>
          <span>
            <IconButton size="small" aria-label={t('moreOptions')} sx={{ minWidth: 44, minHeight: 44 }} disabled>
              <MoreVertIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
